import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import {CALL_STATE} from '@/hooks/useInAppCall'

/**
 * THE CALL ITSELF — the screen a passenger looks at while the phone is ringing.
 *
 * ── ⚠️ AN IN-PAGE CALL NEEDS A PLACE TO HANG UP ───────────────────────────────────
 * That is the whole reason this exists. Handing off to the device's dialler borrows its
 * entire interface: ringing, connecting, mute, end. Placing the call inside the page means
 * building all of it, and the one that cannot be skipped is the way out — a call with no
 * visible end button is a microphone a passenger cannot close.
 *
 * ── ⚠️ IT CANNOT BE DISMISSED WHILE A CALL IS LIVE ────────────────────────────────
 * Closing the modal would leave the call running with nothing on screen saying so. The
 * only exit is End, which hangs up first.
 *
 * ── The shape of it ───────────────────────────────────────────────────────────────
 * Face, name, status, controls — down the middle, which is what every calling screen on
 * every phone looks like, and deliberately identical to the driver portal's. Whoever
 * glances at this needs to know WHO is on the other end before anything else.
 */
export default function CallOverlay({driverName, driverPhoto, state, error, muted, onHangUp, onToggleMute, onRetry}) {
    const failed = state === CALL_STATE.failed
    const connected = state === CALL_STATE.connected
    const ringing = state === CALL_STATE.preparing || state === CALL_STATE.ringing

    const status = failed ? 'Call failed'
        : state === CALL_STATE.preparing ? 'Connecting…'
        : state === CALL_STATE.ringing ? 'Ringing…'
        /* ⚠️ SAID OUT LOUD RATHER THAN HIDDEN. A call gone quiet while the network rebuilds
           is indistinguishable from one that dropped, and someone who thinks it dropped
           hangs up on a call that was about to come back. */
        : state === CALL_STATE.reconnecting ? 'Reconnecting…'
        : 'Connected'

    return (
        <Modal isOpen onClose={() => {}}>
            <div className="flex flex-col items-center gap-6 py-6 text-center max-md:gap-4 max-md:py-4">
                <span className="relative flex h-24 w-24 items-center justify-center max-md:h-16 max-md:w-16">
                    {/* ⚠️ THE RING PULSES ONLY WHILE RINGING. A call placed in a page makes
                        no sound of its own until it is answered, so with nothing moving the
                        screen is indistinguishable from a frozen one. It stops on connect,
                        where a pulse would say "still trying" to someone mid-sentence. */}
                    {ringing && <span className="absolute inset-0 animate-ping rounded-full bg-(--bg-dark)/15"/>}

                    {driverPhoto
                        ? <img src={driverPhoto} alt="" className="relative h-24 w-24 rounded-full object-cover max-md:h-16 max-md:w-16"/>
                        : (
                            <span className="relative flex h-24 w-24 items-center justify-center rounded-full bg-(--bg-grey) text-2xl font-semibold text-(--text-secondary) max-md:text-lg max-md:h-16 max-md:w-16">
                                {(driverName || 'D').charAt(0).toUpperCase()}
                            </span>
                        )}
                </span>

                <div>
                    <p className="mb-0! text-xl font-semibold text-(--text-primary) max-md:text-base">{driverName || 'Your driver'}</p>
                    {/* ⚠️ THE STATUS IS THE ONLY FEEDBACK THERE IS. Silence with no label
                        reads as nothing having happened. */}
                    <p className="mb-0! mt-1 text-sm text-(--text-secondary)">{status}</p>
                </div>

                {error && <p className="mb-0! text-sm text-(--text-red)" role="alert">{error}</p>}

                {failed ? (
                    <div className="flex w-full gap-3">
                        <button
                            type="button"
                            onClick={onHangUp}
                            className="min-h-[52px] flex-1 cursor-pointer rounded-full border border-(--border-color) bg-transparent px-4 text-base font-semibold text-(--text-primary) max-md:text-sm"
                        >
                            Close
                        </button>
                        <button
                            type="button"
                            onClick={onRetry}
                            className="min-h-[52px] flex-1 cursor-pointer rounded-full border-0 bg-(--bg-dark) px-4 text-base font-semibold text-white max-md:text-sm"
                        >
                            Try again
                        </button>
                    </div>
                ) : (
                    <div className="flex items-start gap-8">
                        {/* ⚠️ MUTE ONLY ONCE CONNECTED. Before that there is no audio to
                            mute, and a control that does nothing when pressed teaches
                            people not to trust the one beside it — which here is the only
                            way to end the call. */}
                        <CircleButton
                            label={muted ? 'Unmute' : 'Mute'}
                            onClick={onToggleMute}
                            disabled={!connected}
                            active={muted}
                        >
                            {muted ? <MicOffIcon/> : <MicIcon/>}
                        </CircleButton>

                        <CircleButton label="End" onClick={onHangUp} danger>
                            <EndIcon/>
                        </CircleButton>
                    </div>
                )}
            </div>
        </Modal>
    )
}

/** A round control with its name under it — the shape a call screen uses, so neither
    button needs reading twice. */
function CircleButton({label, onClick, children, disabled = false, danger = false, active = false}) {
    return (
        <span className="flex flex-col items-center gap-2">
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                aria-label={label}
                aria-pressed={active || undefined}
                className={`flex h-16 w-16 cursor-pointer items-center justify-center rounded-full border-0 transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                    danger
                        ? 'bg-(--text-red) text-white'
                        : active
                            ? 'bg-(--text-primary) text-white'
                            : 'bg-(--bg-grey) text-(--text-primary)'
                } max-md:h-12 max-md:w-12`}
            >
                {children}
            </button>
            <span className="text-xs text-(--text-secondary)">{label}</span>
        </span>
    )
}

const stroke = {fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round'}

function MicIcon() {
    return (
        <svg className="h-6 w-6" viewBox="0 0 24 24" {...stroke}>
            <rect x="9" y="2" width="6" height="12" rx="3"/>
            <path d="M5 11a7 7 0 0 0 14 0M12 18v4"/>
        </svg>
    )
}

function MicOffIcon() {
    return (
        <svg className="h-6 w-6" viewBox="0 0 24 24" {...stroke}>
            <path d="M15 5a3 3 0 0 0-6 0v5M9 12a3 3 0 0 0 5 2"/>
            <path d="M5 11a7 7 0 0 0 10.5 6M19 11a7 7 0 0 1-.6 2.8M12 18v4M3 3l18 18"/>
        </svg>
    )
}

/** The handset, turned down — the universal end-call glyph. */
function EndIcon() {
    return (
        <svg className="h-6 w-6 rotate-[135deg]" viewBox="0 0 24 24" {...stroke}>
            <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .3 1.9.6 2.8a2 2 0 0 1-.5 2.1L8 9.9a16 16 0 0 0 6 6l1.3-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.5 2.8.6a2 2 0 0 1 1.8 2.1z"/>
        </svg>
    )
}

export {CallOverlay}
